/**
 * MoveServiceBookingDialog — reschedule a service booking onto a different run
 * of the same product. Loads the booking to learn its product + current run,
 * lists that product's other upcoming runs, flags overlaps against the guest's
 * schedule (the booking's own slot is ignored), and moves on confirm. Capacity
 * is enforced server-side; a full target surfaces as an error toast.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from '@/core';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/theme/components/ui/dialog';
import { Button } from '@/theme/components/ui/button';
import { Badge } from '@/theme/components/ui/badge';
import { LoadingState } from '@/shared/components';
import { CalendarClock, AlertTriangle, ArrowRight } from 'lucide-react';
import { useServiceBooking, useMoveServiceBooking } from '../hooks/useServiceBookings';
import { useServiceProductRuns } from '../hooks/useServiceRuns';
import { findScheduleConflicts } from '../utils/scheduleConflicts';
import type { GuestScheduleItem, ServiceRunListItem } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: string;
  /** The guest's current schedule, for overlap detection. */
  schedule: GuestScheduleItem[];
}

function isoOffset(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function MoveServiceBookingDialog({ open, onOpenChange, bookingId, schedule }: Props) {
  const { t } = useTranslation();
  const [runId, setRunId] = useState<string | null>(null);

  const { data: booking, isLoading: loadingBooking } = useServiceBooking(bookingId, { enabled: open });
  const { data: runs = [], isLoading: loadingRuns } = useServiceProductRuns(
    booking?.product, isoOffset(0), isoOffset(28), { enabled: open && !!booking?.product },
  );
  const move = useMoveServiceBooking();

  const candidates = useMemo(
    () => runs.filter((r) => r.id !== booking?.run),
    [runs, booking?.run],
  );

  const runConflicts = (run: ServiceRunListItem) =>
    findScheduleConflicts(
      { date: run.date, startTime: run.startTime, endTime: run.endTime },
      schedule,
      { ignoreId: bookingId },
    );

  const close = () => { onOpenChange(false); setRunId(null); };

  const confirm = () => {
    if (!runId) return;
    move.mutate({ id: bookingId, newRunId: runId }, { onSuccess: close });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : close())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {booking
              ? t('services.move.title', 'Reschedule {{name}}', { name: booking.productName })
              : t('services.move.titleGeneric', 'Reschedule')}
          </DialogTitle>
        </DialogHeader>

        {loadingBooking ? (
          <LoadingState />
        ) : !booking ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t('services.move.notFound', 'Booking not found.')}
          </p>
        ) : (
          <div className="space-y-4">
            {booking.run && (booking.effectiveDate || booking.runStartTime) && (
              <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm">
                <span className="text-muted-foreground">{t('services.move.current', 'Currently')}: </span>
                <span className="font-medium">
                  {booking.effectiveDate}
                  {booking.runStartTime ? ` · ${booking.runStartTime.slice(0, 5)}` : ''}
                </span>
              </div>
            )}

            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                {t('services.move.pickRun', 'Move to')}
              </p>
              {loadingRuns ? (
                <LoadingState />
              ) : candidates.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  {t('services.move.noRuns', 'No other upcoming runs for this service.')}
                </p>
              ) : (
                <div className="max-h-64 space-y-1.5 overflow-y-auto">
                  {candidates.map((r) => {
                    const conflicts = runConflicts(r);
                    const selected = r.id === runId;
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setRunId(r.id)}
                        className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                          selected ? 'border-ocean-700 bg-ocean-700/5' : 'border-border hover:border-sand-400'
                        }`}
                      >
                        <CalendarClock className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium">
                            {r.date} · {r.startTime.slice(0, 5)}
                            {r.endTime ? `–${r.endTime.slice(0, 5)}` : ''}
                          </p>
                          {(r.origin || r.dest) && (
                            <p className="flex items-center gap-1 text-xs text-muted-foreground">
                              {r.origin}
                              {r.origin && r.dest && <ArrowRight className="h-3 w-3" />}
                              {r.dest}
                            </p>
                          )}
                        </div>
                        {conflicts.length > 0 && (
                          <Badge variant="outline" className="gap-1 text-sun-700">
                            <AlertTriangle className="h-3 w-3" />
                            {t('services.add.overlap', 'Overlap')}
                          </Badge>
                        )}
                        {r.capacity != null && (
                          <span className="font-mono text-[11px] text-muted-foreground">
                            {t('services.add.cap', 'cap {{n}}', { n: r.capacity })}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={confirm} disabled={!runId || move.isPending}>
            {t('services.move.confirm', 'Reschedule')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
